import { useEffect, useRef, useState } from "react";
import { Newspaper, BellRing, Activity, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { markSubscribed } from "@/lib/sparkle";
import { trackEvent } from "@/hooks/useArticleAnalytics";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "nl_nlpop_dismiss";
const SEEN_KEY = "nl_nlpop_seen";
const DISMISS_DAYS = 14;
const DELAY_MS = 5000;

const PERKS = [
  { icon: Newspaper, label: "The Daily Brief", accent: "#A78BFA" },
  { icon: BellRing, label: "Breaking alerts", accent: "#00E5FF" },
  { icon: Activity, label: "Index flips", accent: "#34D399" },
];

export const NewsletterPopup = () => {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    if (armed.current || loading || user) return;
    try {
      if (window.location.pathname.startsWith("/admin")) return;
      if (localStorage.getItem("nl_subscribed")) return;
      if (sessionStorage.getItem(SEEN_KEY)) return;
      const d = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (d && Date.now() - d < DISMISS_DAYS * 86400000) return;
    } catch {
      return;
    }
    armed.current = true;
    (window as any).__nl_email_popup = true;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* noop */ }
      setOpen(true);
      trackEvent("newsletter_popup_view", { path: window.location.pathname });
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [loading, user]);

  const close = (dismissed: boolean) => {
    setOpen(false);
    (window as any).__nl_email_popup = false;
    if (dismissed) {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/leads/capture", {
        email, source_type: "popup_optin", source_page: window.location.pathname,
        interests: ["daily_brief", "alerts"], referrer: document.referrer || null, consent: true,
      });
      setDone(true);
      markSubscribed();
      trackEvent("newsletter_popup_signup", {});
      setTimeout(() => close(false), 2400);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(!done); }}>
      <DialogContent data-testid="newsletter-popup"
        className="max-w-[430px] p-0 overflow-hidden bg-[#07080d] border-white/10 rounded-2xl gap-0">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#7C3AED]/50 to-[#00E5FF]/30" />
          <div className="absolute -top-14 -left-10 h-52 w-52 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(124,58,237,0.2), transparent 65%)" }} />
          <div className="absolute -top-10 right-[-30px] h-44 w-44 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(0,229,255,0.12), transparent 65%)" }} />
        </div>

        {done ? (
          <div className="relative px-7 py-12 text-center" data-testid="newsletter-popup-success">
            <CheckCircle2 className="h-10 w-10 text-[#34D399] mx-auto" />
            <DialogTitle className="font-heading font-black text-white text-xl mt-4">You're in.</DialogTitle>
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">
              The Brief lands tomorrow morning — alerts are live now.
            </p>
          </div>
        ) : (
          <div className="relative px-6 sm:px-7 pt-8 pb-6">
            <img src="/logo-icon.png" alt="NewsLayer" className="h-10 w-10 rounded-xl border border-[#7C3AED]/40 bg-[#7C3AED]/10 p-1.5" />
            <DialogTitle className="font-heading font-black text-white text-[22px] leading-tight tracking-tight mt-4">
              Get the signal, not the noise.
            </DialogTitle>
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">
              Markets, regulation and onchain intelligence in a 5-minute morning read — plus
              breaking alerts and Layer Index flips as they happen.
            </p>

            <div className="grid grid-cols-3 gap-2 mt-5">
              {PERKS.map((p) => (
                <div key={p.label}
                  className="rounded-lg border border-white/[0.07] bg-white/[0.03] px-2 py-2.5 text-center">
                  <p.icon className="h-4 w-4 mx-auto" style={{ color: p.accent }} />
                  <p className="font-mono text-[9.5px] uppercase tracking-wide text-gray-400 mt-1.5">{p.label}</p>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="mt-5 space-y-2.5">
              <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@work.com" data-testid="newsletter-popup-email"
                className="h-11 bg-[#111219] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E5FF]" />
              <Button type="submit" disabled={busy} data-testid="newsletter-popup-submit"
                className="w-full h-11 bg-[#7C3AED] text-white font-bold hover:bg-[#6b2fd6]">
                {busy ? "Subscribing..." : "Subscribe free →"}
              </Button>
            </form>

            <div className="flex items-center justify-between mt-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">
                Free · No spam · Unsubscribe anytime
              </p>
              <button type="button" onClick={() => close(true)} data-testid="newsletter-popup-dismiss"
                className="text-xs text-gray-500 hover:text-white transition-colors">
                Not now
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
